'use client';

import { createContext, useContext, ReactNode, useState, useEffect } from 'react';
import { actionGetUserProfile } from '@/actions/users/get-user-profile';

type User = Awaited<ReturnType<typeof actionGetUserProfile>>;
type UserContextType = {
  user: User | null;
  isLoading: boolean;
  refetch: () => Promise<void>;
};

const UserContext = createContext<UserContextType | null>(null);

export function useUser(): UserContextType {
  const context = useContext(UserContext);
  if (context === null) {
    throw new Error('useUser must be used within a UserProvider');
  }
  return context;
}

export function UserProvider({ children }: { children: ReactNode }) {
  const [user, setUser] = useState<User | null>(null);
  const [isLoading, setIsLoading] = useState(true);

  const refetch = async () => {
    try {
      const profile = await actionGetUserProfile();
      setUser('error' in profile ? null : profile);
    } catch {
      setUser(null);
    } finally {
      setIsLoading(false);
    }
  };

  useEffect(() => {
    refetch();
  }, []);

  return (
    <UserContext.Provider value={{ user, isLoading, refetch }}>
      {children}
    </UserContext.Provider>
  );
}
